import BigNumber from 'bignumber.js'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { NumberUtil } from '@root/shared'
import { mediaMinTablet } from '@root/shared/ui-kit'
import { AmountInputBase } from '@root/shared/ui-kit/inputs/amount-input-base'

import { CurrencyInputQuickVariants } from './currency-input-quick-variants'
import { CurrencyInputUsdtAmount } from './currency-input-usdt-amount'
import { CurrencySwitchButton } from './currency-switch-button'

import type { AsyncStateN } from '@blade/std-lib'
import type { CurrencyModel } from '@root/entities/currency/model'
import type { CurrencyPlaceholderProps } from '@root/entities/currency/ui/currency-icon/CurrencyPlaceholder'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  isMaxAmount: boolean
  currency: CurrencyModel | undefined
  direction: 'source' | 'target'
  placeholderColor: CurrencyPlaceholderProps['color']
  onCurrencyClick?: () => void
  onChange?: (value: string) => void
  value?: string
  balance: BigNumber
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  currencyPriceUsd: AsyncStateN.AsyncStateN<unknown, BigNumber>
  loading?: boolean
} & ClassNameProps

export const CurrencyInput: React.FC<Props> = ({
  isMaxAmount,
  currency,
  direction,
  className,
  onCurrencyClick,
  value = '',
  onChange,
  onClick,
  balance,
  placeholderColor,
  onBlur,
  loading,
  currencyPriceUsd,
}) => {
  const { t } = useTranslation()
  const directionLabel =
    direction === 'source' ? t('trading.you_pay') : t('trading.you_receive')

  const amountNumber = NumberUtil.parseBigNumber(value) ?? BigNumber(0)

  const handleQuickAmount = (percentage: 'max' | number) => {
    if (percentage === 'max') {
      onChange?.(balance.toFormat())
    }
  }

  return (
    <Root onClick={onClick} className={className}>
      <StartContent>
        <DirectionLabel>{directionLabel}</DirectionLabel>
        <AmountContainer>
          {loading ? (
            <Skeleton height={29} paddingBlock={4} width={50} />
          ) : (
            <Input
              onMouseDown={(e) => {
                if (!currency) {
                  e.preventDefault()
                }
              }}
              onTouchStart={(e) => {
                if (!currency) {
                  e.preventDefault()
                }
              }}
              onBlur={onBlur}
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              decimalScale={currency?.decimals ?? 10}
              allowNegative={false}
            />
          )}
        </AmountContainer>
        <CurrencyInputUsdtAmount
          amount={amountNumber}
          currencyPriceUsd={currencyPriceUsd}
        />
      </StartContent>
      <EndContent>
        <CurrencyInputQuickVariants
          onChange={handleQuickAmount}
          isMaxAmount={isMaxAmount}
        />
        <CurrencySwitchButton
          onClick={onCurrencyClick}
          asset={currency}
          placeholderColor={placeholderColor}
        />
        <Balance>
          {t('common.bal')}:{' '}
          <Numeric
            value={balance}
            preset="currency-amount-short"
            currency={currency?.ticker}
          />
        </Balance>
      </EndContent>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  justify-items: stretch;
  align-items: end;

  padding-block: 12px;
  padding-inline: 12px;
  border-radius: 12px;
  background: var(--swap-input-bg);
`

const StartContent = styled.div`
  display: grid;
  grid-template-rows: auto 29px auto;
  gap: 2px;
`

const EndContent = styled.div`
  display: grid;
  justify-items: end;
  align-content: center;
  gap: 2px;
`

const Balance = styled.div`
  color: var(--text-8);
  text-align: right;

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  ${mediaMinTablet(css`
    font-size: 11px;
  `)}
`

const AmountContainer = styled.div`
  justify-items: start;
  display: grid;
  gap: 3px;
`

const Input = styled(AmountInputBase)`
  font-size: 24px;
  font-weight: 700;
  line-height: 24px;

  text-align: start;
  color: var(--text-12);
  background: transparent;
  border: none;
  outline: none;
  padding-inline: 0;
  width: 100%;
`

const DirectionLabel = styled.div`
  color: var(--text-7);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  ${mediaMinTablet(css`
    font-size: 11px;
  `)}
`
